/*
 * ash-slide-hooks.js
 * ---------------------------------------------------------------
 * Page-level hooks for slideshow slides. Loaded ONCE per page,
 * idempotent (each hook guarded by a window.__ash* flag) so
 * duplicate loads are harmless. Previously inlined into every
 * first-slide render — the inline copy was ~10 KB, so on a
 * home page with 2 slideshow sections that was ~20 KB of
 * duplicated bytes inside the HTML on every navigation.
 * ---------------------------------------------------------------
 * Hooks in this file:
 *  1) LCP hero-video HLS attach — Chrome/Firefox/Edge get HLS
 *     adaptive streaming via hls.js so the first frame paints
 *     from a ~50 KB 480p segment instead of ~500 KB of the 1080p
 *     MP4 that Shopify ships as the only single-file source.
 *  2) Viewport-aware <source> pruning — when a slide renders
 *     both a desktop and a mobile <video> (custom_mobile_media),
 *     detach sources from the wrong-viewport <video> so browsers
 *     don't autoplay-buffer 2-5 MB of never-shown bytes.
 *  3) Sound-button click delegation.
 *  4) Drag/press suppression on the video area — Horizon's
 *     slideshow scroller binds mousedown/pointerdown that make
 *     a tap on the video snap the slide back to its neutral
 *     position; we cancel the press at capture phase.
 *  5) Whole-slide click → navigate when a data-ash-slide-link
 *     attribute is present.
 *
 * The hls.js library is loaded lazily by this file — only on
 * non-Safari browsers, and only when there's a video needing HLS.
 * ---------------------------------------------------------------
 * URL of hls.js as a Shopify asset: passed in via the
 * data-ash-hls-src attribute of THIS script's own <script> tag
 * (so we don't need Liquid interpolation inside the JS body).
 */
(function () {
  var selfScript = document.currentScript || (function () {
    var scripts = document.getElementsByTagName('script');
    for (var i = scripts.length - 1; i >= 0; i--) {
      if (scripts[i].src && scripts[i].src.indexOf('ash-slide-hooks') !== -1) return scripts[i];
    }
    return null;
  })();
  var HLS_JS_URL = selfScript ? selfScript.getAttribute('data-ash-hls-src') : null;

  // -------- Hook 1: LCP hero-video HLS attach ---------------------
  if (!window.__ashLcpHlsAttach) {
    window.__ashLcpHlsAttach = true;
    var vids = document.querySelectorAll('video[data-ash-lcp-hls-src]');
    function ashLcpTryPlay(v) {
      if (!v) return;
      try {
        v.muted = true;
        v.defaultMuted = true;
        v.setAttribute('playsinline', '');
        v.setAttribute('webkit-playsinline', '');
      } catch (e) {}
      var markPlaying = function () {
        v.dataset.ashPlaying = '1';
        var host = v.closest('[data-ash-video-host]');
        if (host) host.setAttribute('data-ash-playing', '1');
      };
      var markPaused = function () {
        if (!v.paused) return;
        v.dataset.ashPlaying = '0';
        var host = v.closest('[data-ash-video-host]');
        if (host) host.removeAttribute('data-ash-playing');
      };
      if (!v.__ashLcpPlayingHook) {
        v.__ashLcpPlayingHook = true;
        v.addEventListener('playing', markPlaying);
        v.addEventListener('pause', markPaused);
      }
      var p = v.play();
      if (p && p.then) {
        p.then(markPlaying).catch(function () {});
      } else if (p && p.catch) {
        p.catch(function () {});
      }
    }
    function ashLcpIsDisplayed(v) {
      try {
        var style = window.getComputedStyle(v);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        var rect = v.getBoundingClientRect();
        return rect.width >= 2 && rect.height >= 2;
      } catch (e) {
        return true;
      }
    }
    if (vids.length) {
      var probe = document.createElement('video');
      var nativeHls = !!(probe.canPlayType && probe.canPlayType('application/vnd.apple.mpegurl'));
      // Safari/iOS: native HLS + autoplay attr is unreliable (Low Power Mode,
      // MO softStop races). Explicit play() on the visible LCP twin.
      if (nativeHls) {
        vids.forEach(function (v) {
          if (!ashLcpIsDisplayed(v)) return;
          ashLcpTryPlay(v);
          var tryMp4Fallback = function () {
            if (!ashLcpIsDisplayed(v)) return;
            if (!v.paused && v.readyState >= 2 && v.currentTime > 0.05) return;
            var mp4 = v.getAttribute('data-ash-lcp-mp4-src');
            if (!mp4 || v.dataset.ashLcpMp4Fallback === '1') {
              ashLcpTryPlay(v);
              return;
            }
            v.dataset.ashLcpMp4Fallback = '1';
            try {
              v.querySelectorAll('source').forEach(function (s) {
                try { s.remove(); } catch (e) {}
              });
              v.src = mp4;
              v.load();
            } catch (e) {}
            ashLcpTryPlay(v);
          };
          [60, 200, 500, 1200, 2500].forEach(function (ms) {
            setTimeout(function () {
              if (!ashLcpIsDisplayed(v)) return;
              if (!v.paused && v.currentTime > 0.05) return;
              // NETWORK_NO_SOURCE / empty currentSrc → swap to MP4.
              if (!v.currentSrc || v.networkState === 3 || v.readyState === 0) {
                tryMp4Fallback();
                return;
              }
              ashLcpTryPlay(v);
            }, ms);
          });
        });
      } else if (HLS_JS_URL) {
        var hlsScript = document.createElement('script');
        hlsScript.src = HLS_JS_URL;
        hlsScript.async = true;
        hlsScript.onload = function () {
          if (typeof Hls === 'undefined' || !Hls.isSupported()) return;
          vids.forEach(function (v) {
            if (v.__ashHlsAttached) return;
            if (!ashLcpIsDisplayed(v)) return;
            var url = v.getAttribute('data-ash-lcp-hls-src');
            if (!url) return;
            var hsrc = v.querySelector('source[type*="mpegurl"]');
            if (hsrc && hsrc.parentNode) hsrc.parentNode.removeChild(hsrc);
            try {
              var hls = new Hls({
                abrEwmaDefaultEstimate: 400000,
                startLevel: -1,
                maxBufferLength: 6,
                maxMaxBufferLength: 12,
              });
              hls.loadSource(url);
              hls.attachMedia(v);
              v.__ashHlsAttached = true;
              hls.on(Hls.Events.MANIFEST_PARSED, function (evt, data) {
                if (data && data.levels && data.levels.length) {
                  var minIdx = 0;
                  for (var i = 1; i < data.levels.length; i++) {
                    if (data.levels[i].bitrate < data.levels[minIdx].bitrate) minIdx = i;
                  }
                  hls.startLevel = minIdx;
                  hls.currentLevel = minIdx;
                }
                ashLcpTryPlay(v);
              });
              hls.on(Hls.Events.ERROR, function (evt, data) {
                if (data && data.fatal) {
                  var mp4 = v.getAttribute('data-ash-lcp-mp4-src');
                  if (mp4) {
                    try { hls.destroy(); } catch (e) {}
                    v.src = mp4;
                    v.load();
                    ashLcpTryPlay(v);
                  }
                }
              });
            } catch (e) {}
          });
        };
        hlsScript.onerror = function () {
          vids.forEach(function (v) {
            var mp4 = v.getAttribute('data-ash-lcp-mp4-src');
            if (mp4) { v.src = mp4; v.load(); ashLcpTryPlay(v); }
          });
        };
        document.head.appendChild(hlsScript);
      }
    }
  }

  // -------- Hook 2: viewport-aware <source> pruning ---------------
  if (!window.__ashSlideViewportPrune) {
    window.__ashSlideViewportPrune = true;
    var slideMQ = window.matchMedia('(max-width: 749px)');
    var pruneSlideVideos = function () {
      var isMob = slideMQ.matches;
      var inactiveSel = isMob
        ? 'video.ash-slide-media--desktop-only'
        : 'video.ash-slide-media--mobile-only';
      var activeSel = isMob
        ? 'video.ash-slide-media--mobile-only'
        : 'video.ash-slide-media--desktop-only';

      document.querySelectorAll(inactiveSel).forEach(function (v) {
        if (v.dataset.ashSlideSourcesStashed === '1') return;
        var srcs = Array.prototype.slice.call(v.querySelectorAll('source'));
        if (!srcs.length) return;
        v.dataset.ashSlideSourcesStashed = '1';
        v._ashSlideSources = srcs;
        srcs.forEach(function (s) { s.parentNode && s.parentNode.removeChild(s); });
        try { v.pause(); } catch (e) {}
        try { v.load(); } catch (e) {}
      });

      document.querySelectorAll(activeSel).forEach(function (v) {
        if (v.dataset.ashSlideSourcesStashed !== '1' || !v._ashSlideSources) return;
        v._ashSlideSources.forEach(function (s) { v.appendChild(s); });
        v.dataset.ashSlideSourcesStashed = '0';
        try { v.load(); } catch (e) {}
        var p = v.play(); if (p && p.catch) p.catch(function () {});
      });
      // Lazy manager may have armed before prune finished — resync.
      if (typeof window.__ashVideoLazySync === 'function') {
        try { window.__ashVideoLazySync(); } catch (e) {}
      }
    };
    pruneSlideVideos();
    if (slideMQ.addEventListener) slideMQ.addEventListener('change', pruneSlideVideos);
    else if (slideMQ.addListener) slideMQ.addListener(pruneSlideVideos);
  }

  // -------- Hook 3: sound-button click delegation -----------------
  if (!window.__ashSlideSoundHook) {
    window.__ashSlideSoundHook = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-ash-slide-sound]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var slide = btn.closest('slideshow-slide') || btn.parentElement;
      var video = null;
      if (slide) {
        var candidates = slide.querySelectorAll('video.slide__video');
        for (var vi = 0; vi < candidates.length; vi++) {
          var cs = window.getComputedStyle(candidates[vi]);
          if (cs.display !== 'none' && cs.visibility !== 'hidden') {
            video = candidates[vi];
            break;
          }
        }
        if (!video) video = slide.querySelector('video.slide__video');
      }
      if (!video) return;
      var nowMuted = !video.muted;
      video.muted = nowMuted;
      if (!nowMuted) {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
      btn.setAttribute('aria-pressed', nowMuted ? 'false' : 'true');
    });
  }

  // -------- Hook 4: drag suppression on video area ----------------
  if (!window.__ashSlideDragBlock) {
    window.__ashSlideDragBlock = true;
    function pressIsOnSlideshowVideo(e) {
      var t = e.target;
      if (!t || !t.closest) return false;
      var slide = t.closest('slideshow-slide');
      if (!slide) return false;
      return !!slide.querySelector('video.slide__video');
    }
    function stopVideoPress(e) {
      if (pressIsOnSlideshowVideo(e)) {
        e.stopPropagation();
        e.stopImmediatePropagation && e.stopImmediatePropagation();
      }
    }
    document.addEventListener('mousedown', stopVideoPress, true);
    document.addEventListener('touchstart', stopVideoPress, { capture: true, passive: true });
    document.addEventListener('pointerdown', stopVideoPress, true);
  }

  // -------- Hook 5: whole-slide click → navigate ------------------
  if (!window.__ashSlideLinkClick) {
    window.__ashSlideLinkClick = true;
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('a, button, [role="button"], input, select, textarea, label, slideshow-controls, [data-ash-slide-sound]')) return;
      var slide = t.closest('slideshow-slide');
      if (!slide) return;
      var holder = slide.querySelector('[data-ash-slide-link]');
      if (!holder) return;
      var url = holder.getAttribute('data-ash-slide-link');
      if (!url) return;
      var target = holder.getAttribute('data-ash-slide-link-target') || '_self';
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation && e.stopImmediatePropagation();
      if (target === '_blank') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    }, true);
  }
})();
